import moment from 'moment-timezone';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { Button, Modal, useToastContext } from '@payaca/ambrosia';
import EntityCard from '@payaca/components/entityCard/EntityCard';
import { AlertTooltip } from '@payaca/components/iconTooltip/IconTooltip';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import JobStatusBadge from '../jobStatusBadge/JobStatusBadge';
import ResendJobModal from '../resendJobModal/ResendJobModal';
import RestoreJobModal from '../restoreJobModal/RestoreJobModal';

import { getRegion, getUserRoles } from '@/utils/stateAccessors';
import {
  useCustomerForDeal,
  useDeal,
  useJobContent,
  useProposal,
} from '@payaca/store/hooks/appState';

import { currencyPrice } from '@payaca/helpers/financeHelper';
import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import { getAvailableActionsForJob } from '@payaca/helpers/jobActionsHelper';
import { getJobType } from '@payaca/helpers/jobHelperV2';

import { JobActionType } from '@payaca/types/jobActionsTypes';

import { actions as jobActions } from '@/api/jobs';
import { useSelector } from '@/api/state';

import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import './ProposalCard.sass';

interface Props {
  proposalId: number;
  onClick?: () => void;
  onDealUpdateSuccess?: () => void;
}

// NOTE: This card is only useable for proposals on a V2 deal. Using this for a V1 deal proposal will render incorrect quickactions
const ProposalCard: FC<PropsWithChildren<Props>> = ({
  proposalId,
  onClick,
  onDealUpdateSuccess,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { pushError } = useToastContext();
  const userRoles = useSelector(getUserRoles);
  const proposal = useProposal(proposalId);
  const jobContent = useJobContent(proposal?.jobContentId);
  const customer = useCustomerForDeal(proposal?.dealId);
  const deal = useDeal(proposal?.dealId);
  const [isDuplicatingJob, setIsDuplicatingJob] = useState(false);
  const [isArchivingJob, setIsArchivingJob] = useState(false);
  const [isMarkingJobAccepted, setIsMarkingJobAccepted] = useState(false);
  const [isMarkingJobDeclined, setIsMarkingJobDeclined] = useState(false);
  const [showResendJobModal, setShowResendJobModal] = useState(false);
  const [showRestoreJobModal, setShowRestoreJobModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const region = useSelector(getRegion);
  const shortDateRegionalFormat = useMemo(
    () => getInternationalMomentDateFormatByRegion(DateFormats.SHORT, region),
    [region]
  );

  const markJobAccepted = useCallback(
    (proposalId: number) => {
      setIsMarkingJobAccepted(true);
      dispatch(
        jobActions.acceptQuote(proposalId, () => {
          onDealUpdateSuccess?.();
          setIsMarkingJobAccepted(false);
        })
      );
    },
    [dispatch, onDealUpdateSuccess]
  );

  const markJobDeclined = useCallback(
    (proposalId: number) => {
      setIsMarkingJobDeclined(true);
      dispatch(
        jobActions.declineJob(proposalId, () => {
          onDealUpdateSuccess?.();
          setIsMarkingJobDeclined(false);
        })
      );
    },
    [dispatch, onDealUpdateSuccess]
  );

  const duplicateJob = useCallback(
    (proposalId: number) => {
      setIsDuplicatingJob(true);
      dispatch(
        jobActions.duplicateJob(proposalId, (error: any, response: any) => {
          if (error) {
            pushError(
              error?.message ||
                'Could not duplicate this proposal. Please try again or contact support.'
            );
          } else {
            history.push('/'); // this is needed so the page actually reloads - otherwise it sees that the route hasn't changed and doesn't do anything

            history.push(`/quotes/${response.newJobId}`);
          }
          setIsDuplicatingJob(false);
        })
      );
    },
    [dispatch, history, pushError]
  );

  const archiveJob = useCallback((_proposalId: number) => {
    setShowArchiveModal(true);
  }, []);

  const confirmArchiveJob = useCallback(() => {
    setIsArchivingJob(true);
    dispatch(
      jobActions.archiveJob(proposalId, () => {
        setIsArchivingJob(false);
        setShowArchiveModal(false);
        onDealUpdateSuccess?.();
      })
    );
  }, [dispatch, proposalId, onDealUpdateSuccess]);

  const goToSendJob = useCallback(
    (proposalId: number) => {
      history.push(`/quotes/${proposalId}?view=send`);
    },
    [history]
  );

  const proposalQuickActionDefinitions: { [key: string]: any } = useMemo(() => {
    let definitions = {};
    if (userHasRequiredPermission(userRoles, [JobPermissions.DUPLICATE_JOB])) {
      definitions = {
        [JobActionType.DUPLICATE]: {
          actionName: 'Duplicate',
          actionBehaviour: duplicateJob,
          isActionProcessing: isDuplicatingJob,
        },
      };
    }
    definitions = {
      ...definitions,
      [JobActionType.MARK_AS_ACCEPTED]: {
        actionName: 'Mark as accepted',
        actionBehaviour: markJobAccepted,
        isActionProcessing: isMarkingJobAccepted,
      },
      [JobActionType.MARK_AS_DECLINED]: {
        actionName: 'Mark as declined',
        actionBehaviour: markJobDeclined,
        isActionProcessing: isMarkingJobDeclined,
      },
      [JobActionType.ARCHIVE]: {
        actionName: 'Archive',
        actionBehaviour: archiveJob,
        isActionProcessing: isArchivingJob,
      },
      [JobActionType.SEND]: {
        actionName: 'Send',
        actionBehaviour: goToSendJob,
      },
      [JobActionType.RESEND]: {
        actionName: 'Resend',
        actionBehaviour: () => setShowResendJobModal(true),
      },
      [JobActionType.UNARCHIVE]: {
        actionName: 'Restore',
        actionBehaviour: () => setShowRestoreJobModal(true),
      },
    };
    return definitions;
  }, [
    duplicateJob,
    isDuplicatingJob,
    markJobAccepted,
    isMarkingJobAccepted,
    markJobDeclined,
    isMarkingJobDeclined,
    archiveJob,
    isArchivingJob,
    goToSendJob,
    userRoles,
  ]);

  const availableActions = useMemo(
    () =>
      proposal && deal && customer
        ? getAvailableActionsForJob(proposal, deal, customer, [], [], [])
        : [],
    [proposal, deal, customer]
  );

  const quickActions = useMemo(() => {
    return availableActions
      .map((availableAction) => proposalQuickActionDefinitions[availableAction])
      .filter((x) => !!x);
  }, [availableActions, proposalQuickActionDefinitions]);

  if (!proposal || !jobContent) return null;

  const dateElement = () => {
    if (proposal.validUntil && !proposal.acceptedAt) {
      return (
        <>
          <dt>Valid until</dt>
          <dd>
            <time dateTime={proposal?.validUntil?.toString()}>
              {moment(proposal.validUntil).format(shortDateRegionalFormat)}
            </time>
          </dd>
        </>
      );
    } else if (proposal.acceptedAt) {
      return (
        <>
          <dt>Accepted at</dt>
          <dd>
            <time dateTime={proposal?.acceptedAt?.toString()}>
              {moment(proposal.acceptedAt).format(shortDateRegionalFormat)}
            </time>
          </dd>
        </>
      );
    }
  };

  const issue = (() => {
    if (proposal.bouncedAt) {
      return 'E-mail has bounced';
    }
  })();

  return (
    <>
      <EntityCard
        className="proposal-card"
        onClick={onClick}
        quickActionsConfig={{
          recordId: proposalId,
          renderDisabledIfNoActions: true,
          quickActions: quickActions,
        }}
      >
        <ResponsiveViewWrapper
          className="proposal-card-content"
          downBreakpointSm={500}
        >
          <>
            <JobStatusBadge status={proposal.readableStatus} />
            <dl className="price">
              <dt>
                {getJobType(proposal)}
                {` total (inc tax)`}
              </dt>
              <dd>{currencyPrice(jobContent.total, region)}</dd>
            </dl>
            <dl className="date">{dateElement()}</dl>
            {issue && (
              <div className={'card-issue'}>
                <AlertTooltip tooltipText={issue} />
              </div>
            )}
          </>
        </ResponsiveViewWrapper>
      </EntityCard>
      <ResendJobModal
        isOpen={showResendJobModal}
        onClose={() => {
          setShowResendJobModal(false);
          onDealUpdateSuccess?.();
        }}
        jobId={proposalId}
      />
      <RestoreJobModal
        isOpen={showRestoreJobModal}
        jobId={proposalId}
        onClose={() => setShowRestoreJobModal(false)}
        onRestoreJobCallback={() => {
          setShowRestoreJobModal(false);
          onDealUpdateSuccess?.();
        }}
      />
      <Modal
        isOpen={showArchiveModal}
        title="Archive job"
        onClose={() => setShowArchiveModal(false)}
        size="sm"
      >
        <Modal.Body>
          Archiving this job will also archive any related quotes or invoices.
          Are you sure you want to continue?
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="outline"
              onClick={() => setShowArchiveModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={confirmArchiveJob}>Archive</Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProposalCard;
